/**
 * `/planos` — a vitrine pública.
 *
 * É a única tela do produto que abre sem sessão além do login: quem ainda não
 * tem conta precisa ver preço antes de criar uma. Por isso ela usa
 * `billing.catalog`, que é público, e não toca em nada de workspace.
 *
 * O que a tela NÃO faz, e é o ponto inteiro:
 *  - não divide preço por mês (o `monthlyEquivalentCents` vem do servidor);
 *  - não calcula desconto (o `discountPercent` vem do servidor);
 *  - não escolhe o plano em destaque (é o `badge` do servidor);
 *  - não monta a lista de recursos da comparação (é o `featureKeys` do
 *    servidor — a tela só traduz chave em palavra).
 *
 * E o botão: aqui ele **não cobra e não contrata**. Sem sessão, leva ao
 * cadastro; com sessão, leva à aba de plano do workspace, que é onde o pedido é
 * registrado — lá existe UM workspace e UM papel para conferir. Pedir plano na
 * vitrine com a pessoa tendo três workspaces seria escolher por ela em silêncio.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { buttonVariants, Card, cn, EmptyState, ErrorState, Skeleton, useTheme } from '@mgdlead/ui';
import { CreditCard, Moon, Sun } from 'lucide-react';

import { BrandMark } from '@mgdlead/ui';
import { AvisoDePagamento } from '../features/billing/AvisoDePagamento';
import { CicloSelector } from '../features/billing/CicloSelector';
import { PlanoCard } from '../features/billing/PlanoCard';
import { TabelaComparativa } from '../features/billing/TabelaComparativa';
import {
  algumCheckoutDisponivel,
  checkoutDoCiclo,
  rotuloDoBotao,
} from '../features/billing/billing-ui';
import { workspacePath } from '../features/workspace/navigation';
import { useSession } from '../auth/useSession';
import { useBillingCatalog, useWorkspaces } from '../lib/api';
import type { BillingInterval, CatalogPlan } from '../lib/api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const CICLO_INICIAL: BillingInterval = 'MONTHLY';

export function PlanosPage() {
  useDocumentTitle('Planos');
  const [interval, setInterval] = useState<BillingInterval>(CICLO_INICIAL);
  const catalogo = useBillingCatalog();

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <TopoPublico />

      <main className="mx-auto flex w-full max-w-300 flex-1 flex-col gap-6 px-4 py-6 md:py-10">
        <header className="flex flex-col items-center gap-3 text-center">
          <h1 className="text-xl font-semibold tracking-tight md:text-2xl">
            Planos do MGDLead
          </h1>
          <p className="max-w-prose text-sm text-muted-foreground">
            Monitorar os grupos, distribuir os leads e saber quanto cada entrada custou. Escolha
            pelo tamanho da sua operação — dá para trocar depois.
          </p>
          {catalogo.isSuccess && catalogo.data.plans.length > 0 ? (
            <CicloSelector
              plans={catalogo.data.plans}
              value={interval}
              onChange={setInterval}
              className="mt-1 w-full sm:w-auto"
            />
          ) : null}
        </header>

        {catalogo.isPending ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-72 w-full max-sm:hidden" />
            <Skeleton className="h-72 w-full max-lg:hidden" />
          </div>
        ) : catalogo.isError ? (
          <Card padded={false}>
            <ErrorState
              title="Não foi possível carregar os planos"
              description="A tabela de preços vem do servidor. Preço inventado na tela seria pior do que tela vazia — então preferimos não mostrar nenhum."
              onRetry={() => void catalogo.refetch()}
            />
          </Card>
        ) : catalogo.data.plans.length === 0 ? (
          <Card padded={false}>
            <EmptyState
              kind="no-data"
              icon={CreditCard}
              title="Nenhum plano publicado"
              description="O servidor não devolveu plano público nenhum no momento."
            />
          </Card>
        ) : (
          <>
            {/*
              Celular: os cartões viram um carrossel de rolagem horizontal com
              encaixe (um cartão por vez, 85% da largura), e não uma pilha de
              cinco blocos que empurra a tabela comparativa para dois polegares
              de distância. Do `sm` para cima vira grade normal.
            */}
            <div
              className={[
                'flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2',
                '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                'sm:grid sm:snap-none sm:grid-cols-2 sm:overflow-visible lg:grid-cols-3',
              ].join(' ')}
            >
              {catalogo.data.plans.map((plan) => (
                <PlanoCard
                  key={plan.code}
                  plan={plan}
                  interval={interval}
                  featureKeys={catalogo.data.featureKeys}
                  comparativoAbaixo
                  className="w-[85%] shrink-0 snap-center sm:w-auto sm:shrink"
                  acao={<AcaoDaVitrine plan={plan} interval={interval} />}
                />
              ))}
            </div>

            {/* O cartão seguinte já aparece pela metade; a frase é para quem não vê a borda. */}
            <p className="-mt-4 text-center text-xs text-muted-foreground sm:hidden">
              Arraste para o lado para ver os {catalogo.data.plans.length} planos.
            </p>

            <AvisoDePagamento
              checkoutDisponivel={algumCheckoutDisponivel(catalogo.data.plans, interval)}
            />

            <TabelaComparativa
              plans={catalogo.data.plans}
              featureKeys={catalogo.data.featureKeys}
              interval={interval}
            />
          </>
        )}
      </main>

      <footer className="border-t border-border px-4 py-6 text-center text-xs text-muted-foreground">
        Preços em reais. Os limites de cada plano são aplicados pelo servidor — o painel mostra o
        consumo real contra eles dentro do seu workspace.
      </footer>
    </div>
  );
}

/* ========================================================================== */
/*  Topo                                                                       */
/* ========================================================================== */

function TopoPublico() {
  const { status } = useSession();
  const { resolvedTheme, setTheme } = useTheme();

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-border bg-background px-4">
      <Link
        to="/"
        className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="MGDLead — início"
      >
        <BrandMark />
      </Link>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          aria-label={resolvedTheme === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
          className="inline-flex size-11 items-center justify-center rounded-md border border-transparent text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:size-9"
        >
          {resolvedTheme === 'dark' ? (
            <Sun aria-hidden="true" className="size-4" />
          ) : (
            <Moon aria-hidden="true" className="size-4" />
          )}
        </button>

        {status === 'authenticated' ? (
          <Link
            to="/"
            className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-surface px-3 text-sm font-medium hover:bg-muted max-md:min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Ir para o painel
          </Link>
        ) : (
          <Link
            to="/login"
            className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-surface px-3 text-sm font-medium hover:bg-muted max-md:min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Entrar
          </Link>
        )}
      </div>
    </header>
  );
}

/* ========================================================================== */
/*  O botão de cada plano                                                      */
/* ========================================================================== */

/**
 * O rótulo é `rotuloDoBotao` — o MESMO da aba de plano do workspace: quem tem
 * `trialDays > 0` oferece o teste, o resto diz "Assinar". Quem decide é o
 * número do servidor, nunca o código do plano.
 *
 * O destino muda com a sessão, e só ele:
 *  - sem conta → `/cadastro`, guardando o plano escolhido na URL;
 *  - com conta → a aba de plano do workspace, onde o pedido é registrado.
 */
function AcaoDaVitrine({ plan, interval }: { plan: CatalogPlan; interval: BillingInterval }) {
  const { status } = useSession();
  const workspaces = useWorkspaces();
  const rotulo = rotuloDoBotao(plan);
  const destaque = plan.badge !== null;
  const classe = cn(
    buttonVariants({ variant: destaque ? 'primary' : 'secondary', block: true }),
  );

  const checkout = checkoutDoCiclo(plan, interval);

  /*
    ⚠️ **Quem não tem conta NÃO vai para o checkout**, mesmo com o link no ar.

    A cobrança acontece fora daqui e nada avisa o MGDLead quando alguém paga —
    não existe webhook da Kiwify neste produto. Um anônimo que paga primeiro
    fica com o dinheiro do lado de lá e sem nenhuma conta deste lado para
    liberar: o pior desfecho possível, e o único que a tela consegue evitar
    sozinha. Então a ordem é conta primeiro, pagamento depois.
  */
  if (status === 'authenticated' && checkout !== null) {
    return (
      <a href={checkout} target="_blank" rel="noopener noreferrer" className={classe}>
        {rotulo}
      </a>
    );
  }

  // Navegação é LINK, não botão com `onClick`: abre em nova aba, o navegador
  // mostra o destino e o teclado enxerga o que é. O visual é o do botão.
  const primeiro = workspaces.data?.[0];
  const destino =
    status !== 'authenticated'
      ? `/cadastro?plano=${plan.code}`
      : primeiro === undefined
        ? '/onboarding'
        : `${workspacePath(primeiro.slug, 'assinatura')}?plano=${plan.code}`;

  return (
    <Link to={destino} className={classe}>
      {rotulo}
    </Link>
  );
}
